import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useField } from 'react-final-form';
import { ConfirmationModal, Table, header2, smallBody } from '@ubeya/shared-web/components';
import editable from '@ubeya/shared-web/style/editable';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import {
  TotalTimeRenderer,
  InTimeRenderer,
  OutTimeRenderer,
  EmployeeSignoffRenderer,
  ClientSignoffRenderer
} from '../CellRenderers';

const Wrapper = styled.div`
  margin-top: 30px;
`;

const StyledTable = styled(Table)`
  border-radius: 3px;
  max-height: 102px;

  .row {
    ${editable};
  }

  .row .cell .actual {
    ${header2};

    .select-label,
    .select-input {
      ${header2};
    }
  }

  .row .cell .schedule {
    ${smallBody};
    margin-left: 8px;
  }
`;

const ShiftTable = ({ data, readOnly = false }) => {
  const { t } = useContext(ConfigContext);
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const {
    input: { onChange: onChangeStartTime }
  } = useField('actualIn');

  const {
    input: { onChange: onChangeEndTime }
  } = useField('actualOut');

  const updateEndTime = useCallback(({ newTime }) => onChangeEndTime(newTime), [onChangeEndTime]);
  const updateStartTime = useCallback(({ newTime }) => onChangeStartTime(newTime), [onChangeStartTime]);

  const columns = useMemo(
    () => [
      {
        Header: t('in'),
        dataKey: 'in',
        Cell: InTimeRenderer,
        columnData: { readOnly, updateStartTime, updateEndTime, setIsPromptOpen }
      },
      {
        Header: t('out'),
        dataKey: 'out',
        Cell: OutTimeRenderer,
        columnData: { readOnly, updateEndTime, setIsPromptOpen }
      },
      { Header: t('totalTime'), dataKey: 'total', Cell: TotalTimeRenderer },
      // We don't currently get payroll data for shift table
      // { Header: t('expenses'), dataKey: 'expensesRate' },
      // { Header: t('hourlyRate'), dataKey: 'hourlyRate' },
      // { Header: t('totalSalary'), dataKey: 'totalSalary' },
      {
        Header: t('employeeSignoff'),
        dataKey: 'employeeApproved',
        Cell: EmployeeSignoffRenderer,
        cellRendererProps: { style: { height: '100%', width: '100%' } },
        cellWrapperProps: { style: { padding: '2px' } }
      },
      {
        Header: t('clientSignoff'),
        dataKey: 'clientApproved',
        Cell: ClientSignoffRenderer,
        cellRendererProps: { style: { height: '100%', width: '100%' } },
        cellWrapperProps: { style: { padding: '2px' } }
      }
    ],
    [t, readOnly, updateStartTime, updateEndTime]
  );

  return (
    <Wrapper>
      <StyledTable columns={columns} data={[data]} shadow={false} />
      {isPromptOpen && (
        <ConfirmationModal
          onClose={() => setIsPromptOpen(false)}
          title={t('areYouSureYouEnteredTheCorrectTime')}
          body={t('theShiftDurationWillBeHours', { duration: isPromptOpen.duration })}
          onConfirm={() => {
            isPromptOpen.onConfirm?.();
            setIsPromptOpen(false);
          }}
          confirmText={t('ok')}
          showCancelButton
        />
      )}
    </Wrapper>
  );
};

export default ShiftTable;
